import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, message, Card } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';
import './PasscodeLock.scss';

const { Title, Text } = Typography;

const PasscodeLock = ({ 
	onPasscodeComplete, 
	isSetupMode = false, 
	onCancel,
	title,
	subtitle,
	maxLength = 6,
	isModal = false // Add prop to detect if in modal
}) => {
	const { themeColor, theme } = useTheme();
	const [currentPasscode, setCurrentPasscode] = useState('');
	const [setupStep, setSetupStep] = useState(1); // 1: first passcode, 2: confirm passcode
	const [firstPasscode, setFirstPasscode] = useState('');
	const [isShaking, setIsShaking] = useState(false);
	const [animationState, setAnimationState] = useState(''); // 'success', 'error', ''

	const keypadNumbers = [
		[1, 2, 3],
		[4, 5, 6], 
		[7, 8, 9],
		['', 0, 'delete']
	];

	// Handle number input
	const handleNumberPress = (number) => {
		if (number === '') return;
		
		if (number === 'delete') {
			setCurrentPasscode(prev => prev.slice(0, -1));
			return;
		}

		if (currentPasscode.length < maxLength) {
			const newPasscode = currentPasscode + number.toString();
			setCurrentPasscode(newPasscode);
			
			// Auto-submit when max length is reached
			if (newPasscode.length === maxLength) {
				setTimeout(() => {
					if (isSetupMode) {
						handleSetupPasscode(newPasscode);
					} else {
						onPasscodeComplete(newPasscode);
					}
				}, 300);
			}
		}
	};

	// Handle setup mode (two-step passcode creation)
	const handleSetupPasscode = (passcode = currentPasscode) => {
		if (passcode.length < 4) {
			message.warning('Passcode must be at least 4 digits');
			setAnimationState('error');
			setIsShaking(true);
			setTimeout(() => {
				setIsShaking(false);
				setAnimationState('');
			}, 500);
			return;
		}

		if (setupStep === 1) {
			setFirstPasscode(passcode);
			setSetupStep(2);
			setAnimationState('success');
			setTimeout(() => {
				setCurrentPasscode('');
				setAnimationState('');
			}, 600);
		} else {
			// Confirm passcode
			if (passcode === firstPasscode) {
				setAnimationState('success');
				setTimeout(() => {
					onPasscodeComplete(passcode);
					message.success('Passcode set successfully!');
				}, 600);
			} else {
				setAnimationState('error');
				message.error('Passcodes do not match. Please try again.');
				setTimeout(() => {
					setSetupStep(1);
					setFirstPasscode('');
					setCurrentPasscode('');
					setIsShaking(false);
					setAnimationState('');
				}, 800);
				setIsShaking(true);
			}
		}
	};

	const resetPasscode = () => {
		setCurrentPasscode('');
		if (isSetupMode) {
			setSetupStep(1);
			setFirstPasscode('');
		}
	};

	// Create dots for passcode display
	const renderPasscodeDots = () => {
		const dots = [];
		for (let i = 0; i < maxLength; i++) {
			const isFilled = i < currentPasscode.length;
			const dotStyle = {
				backgroundColor: isFilled ? (
					animationState === 'error' ? '#ff4d4f' : 
					animationState === 'success' ? '#52c41a' : 
					themeColor
				) : undefined,
				borderColor: animationState === 'error' ? '#ff4d4f' : 
				              animationState === 'success' ? '#52c41a' : 
				              isFilled ? themeColor : undefined
			};
			
			dots.push(
				<div 
					key={i} 
					className={`passcode-dot ${isFilled ? 'filled' : ''} ${animationState}`}
					style={dotStyle}
				/>
			);
		}
		return dots;
	};

	// Trigger shake animation on wrong passcode
	useEffect(() => {
		if (isShaking) {
			const timer = setTimeout(() => setIsShaking(false), 500);
			return () => clearTimeout(timer);
		}
	}, [isShaking]);

	return (
		<div className={`passcode-lock ${isModal ? 'passcode-lock-modal' : ''} ${(title || subtitle) ? 'no-header' : ''} ${animationState ? `passcode-${animationState}` : ''}`}>
			{/* Only show header if title/subtitle not provided externally */}
			{(!title && !subtitle) && (
				<div className="passcode-lock-header">
					<Space direction="vertical" size="small" style={{ textAlign: 'center', width: '100%' }}>
						<div className="passcode-icon">
							<KeyOutlined style={{ 
								fontSize: '48px', 
								color: isModal ? themeColor : 'white',
								filter: `drop-shadow(0 2px 8px ${themeColor}40)`
							}} />
						</div>
						<Title level={3} style={{ 
							margin: 0, 
							color: isModal ? undefined : 'white',
							textShadow: isModal ? 'none' : '0 2px 4px rgba(0,0,0,0.3)'
						}}>
							{isSetupMode ? 'Set Lock Passcode' : 'Enter Lock Passcode'}
						</Title>
						<Text style={{ 
							color: isModal ? undefined : 'rgba(255,255,255,0.8)',
							fontSize: '16px'
						}}>
							{isSetupMode 
								? (setupStep === 1 ? `Enter a ${maxLength}-digit passcode` : 'Confirm your passcode')
								: 'Enter your passcode to unlock'
							}
						</Text>
					</Space>
				</div>
			)}

			<div className="passcode-display-container">
				<div className={`passcode-display ${isShaking ? 'shake' : ''}`}>
					{renderPasscodeDots()}
				</div>
				<div className="passcode-progress">
					<Text className="progress-text">
						{currentPasscode.length} of {maxLength} digits
					</Text>
					<div className="progress-bar">
						<div 
							className="progress-fill"
							style={{ 
								width: `${(currentPasscode.length / maxLength) * 100}%`,
								backgroundColor: themeColor 
							}}
						/>
					</div>
				</div>
			</div>

			<div className="passcode-keypad">
				{keypadNumbers.map((row, rowIndex) => (
					<div key={rowIndex} className="keypad-row">
						{row.map((number, colIndex) => (
							<Button
								key={`${rowIndex}-${colIndex}`}
								className={`keypad-button ${
									number === 'delete' ? 'delete-button' : 
									number === '' ? 'empty-button' : 'number-button'
								}`}
								onClick={() => handleNumberPress(number)}
								disabled={number === ''}
								size="large"
								style={{
									borderColor: isModal ? undefined : 'rgba(255,255,255,0.2)',
									backgroundColor: isModal ? undefined : 'rgba(255,255,255,0.1)',
									color: isModal ? undefined : 'white'
								}}
							>
								{number === 'delete' ? (
									<DeleteOutlined style={{ fontSize: '20px' }} />
								) : (
									<span style={{ fontSize: '24px', fontWeight: 'bold' }}>{number}</span>
								)}
							</Button>
						))}
					</div>
				))}
			</div>

			<div className="passcode-lock-actions">
				<Space size="large">
					{onCancel && (
						<Button 
							icon={<ArrowLeftOutlined />} 
							onClick={onCancel}
							size="large"
							className={`action-button ${isModal ? 'modal-button' : ''}`}
							type={isModal ? "default" : "primary"}
						>
							Back
						</Button>
					)}
					<Button 
						onClick={resetPasscode}
						size="large"
						className={`action-button ${isModal ? 'modal-button' : ''}`}
						type={isModal ? "default" : "primary"}
					>
						Clear All
					</Button>
				</Space>
			</div>

			{/* Setup Progress Indicator */}
			{isSetupMode && (
				<div className="setup-progress">
					<div className={`progress-step ${setupStep >= 1 ? 'active' : ''}`}>
						<div className="step-number">1</div>
						<Text>Create</Text>
					</div>
					<div className="progress-line" />
					<div className={`progress-step ${setupStep >= 2 ? 'active' : ''}`}>
						<div className="step-number">2</div>
						<Text>Confirm</Text>
					</div>
				</div>
			)}
		</div>
	);
};

export default PasscodeLock;